import styled from '@emotion/styled';
import { ArrowRight, Search } from '@mui/icons-material';
import { groupBy, uniqBy } from 'es-toolkit';
import { ReactEventHandler, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import createPersistedState from 'use-persisted-state';
import { SongPreview } from '~/interfaces';
import { Badge } from '~/modules/Elements/Badge';
import { Flag } from '~/modules/Elements/Flag';
import { Input } from '~/modules/Elements/Input';
import { typography } from '~/modules/Elements/cssMixins';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListener } from '~/modules/GameEvents/hooks';
import RemoteMicClient from '~/modules/RemoteMic/Network/Client';
import { transportErrorReason } from '~/modules/RemoteMic/Network/Client/NetworkClient';
import { NetworkSongListMessage } from '~/modules/RemoteMic/Network/messages';
import SongDao from '~/modules/Songs/SongsService';
import { useLanguageList } from '~/modules/Songs/hooks/useLanguageList';
import useSongIndex from '~/modules/Songs/hooks/useSongIndex';
import useBaseUnitPx from '~/modules/hooks/useBaseUnitPx';
import isE2E from '~/modules/utils/isE2E';
import LanguageFilter from '~/routes/RemoteMic/Panels/RemoteSongList/LanguageFilter';
import { useMySongList } from '~/routes/RemoteMic/Panels/RemoteSongList/useMySongList';
import { ConnectionStatuses } from '~/routes/RemoteMic/RemoteMic';
import usePermissions from '~/routes/RemoteMic/hooks/usePermissions';
import { CustomVirtualization } from '~/routes/SingASong/SongSelection/Components/CustomVirtualization';
import { searchList } from '~/routes/SingASong/SongSelection/Hooks/useSongListFilter';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
}
const useExcludedLanguages = createPersistedState<string[]>('remote-mic-excluded-languages');
const useSelectedList = createPersistedState<'list' | 'queue'>('remote-mic-selected-song-list');
const useSearchState = createPersistedState<string>('remote-mic-song-list-search');

const getMainArtistName = (artist: string) => artist.split(' feat')[0];
const isArtistPresent = (a: string, b: string[]) => b.some((artist) => artist.toLowerCase() === a.toLowerCase());

function RemoteSongList({ connectionStatus }: Props) {
  const [keyboard] = useEventListener(events.remoteKeyboardLayout, true) ?? [];
  const permissions = usePermissions();
  const originalSongList = useSongIndex();
  const [tab, setTab] = useSelectedList('list');
  const [search, setSearch] = useSearchState('');
  const [excludedLanguages, setExcludedLanguages] = useExcludedLanguages([]);
  const { savedSongList, toggleSong } = useMySongList();

  const [overrides, setOverrides] = useState<NetworkSongListMessage | undefined>();
  useEffect(() => {
    if (connectionStatus === 'connected' && overrides === undefined) {
      RemoteMicClient.getSongList().then(setOverrides).catch(console.warn);
    }
  }, [overrides, connectionStatus]);

  const songList = useMemo(
    () =>
      uniqBy(
        originalSongList.data
          .map((song) => ({ ...song, id: SongDao.generateSongFile(song) }))
          .filter((song) => !(overrides?.deleted ?? []).includes(song.id))
          .map(
            (song) =>
              ({
                id: song.id,
                artist: song.artist,
                title: song.title,
                video: song.video,
                language: song.language,
                search: song.search,
              }) as NetworkSongListMessage['custom'][number] & Pick<SongPreview, 'id'>,
          )
          .concat(...(overrides?.custom ?? []).map((song) => ({ ...song, id: SongDao.generateSongFile(song) }))),
        (song) => song.id,
      ),
    [originalSongList.data, overrides],
  );

  const changeTab = (tab: 'list' | 'queue') => {
    setTab(tab);
    setSearch('');
  };
  const filteredSongList = useMemo(() => {
    if (tab === 'list') {
      const searchedList = searchList(songList, search);
      if (searchedList.length !== songList.length) {
        return searchedList;
      } else {
        return songList.filter((song) => !song.language.every((songLang) => excludedLanguages.includes(songLang!)));
      }
    } else {
      const addedSongList = savedSongList.map((id) => songList.find((song) => song.id === id)!).filter(Boolean);
      return searchList(addedSongList, search);
    }
  }, [search, songList, tab, savedSongList, excludedLanguages]);

  const groupedSongList = useMemo(() => {
    if (filteredSongList.length < (isE2E() ? 5 : 500)) {
      return filteredSongList;
    }
    const groups = groupBy(filteredSongList, (song) => getMainArtistName(song.artist).toLowerCase());

    return Object.values(groups)
      .map((group) => (group.length >= (isE2E() ? 2 : 3) ? [group] : group))
      .flat();
  }, [filteredSongList]);

  const [expandedArtists, setExpandedArtists] = useState<string[]>([]);
  useLayoutEffect(() => {
    setExpandedArtists([]);
  }, [search, songList, tab, excludedLanguages]);

  const itemsToRender = useMemo(() => {
    const itemsToRender: typeof groupedSongList = [];

    for (const song of groupedSongList) {
      itemsToRender.push(song);
      if (Array.isArray(song) && expandedArtists.includes(getMainArtistName(song[0].artist))) {
        itemsToRender.push(...song);
      }
    }
    return itemsToRender;
  }, [groupedSongList, expandedArtists]);

  const languages = useLanguageList(songList);
  const selectedLanguages = languages.length - excludedLanguages.length;

  const unit = useBaseUnitPx();

  return (
    <Container className="overflow-hidden">
      <TopBar className="pt-1">
        <SearchInput
          focused={false}
          label={<Search />}
          placeholder="Search the list…"
          value={search}
          onChange={(value) => setSearch(value)}
          data-test="search-input"
        />
        <Tabs>
          <Tab data-test="all-songs-button" data-active={tab === 'list'} onClick={() => changeTab('list')}>
            All songs
          </Tab>
          <Tab data-test="your-list-button" data-active={tab === 'queue'} onClick={() => changeTab('queue')}>
            Your list ({savedSongList.length})
          </Tab>
          <LanguageFilter
            excludedLanguages={excludedLanguages}
            languageList={languages}
            onListChange={setExcludedLanguages}>
            {({ open }) => (
              <Tab
                className="flex-grow-[0.3]!"
                onClick={open}
                data-test="song-language-filter"
                data-active={excludedLanguages.length > 0 && tab === 'list' ? true : undefined}>
                🇺🇳{selectedLanguages < languages.length && <Badge>{selectedLanguages}</Badge>}
              </Tab>
            )}
          </LanguageFilter>
        </Tabs>
      </TopBar>
      <CustomVirtualization
        forceRenderItem={-1}
        overScan={200}
        components={{}}
        context={{}}
        groupSizes={[itemsToRender.length]}
        groupHeaderHeight={1}
        groupContent={() => null}
        itemHeight={unit * 7}
        itemContent={(index, _groupIndex, itemProps) => {
          const song = itemsToRender[index];

          if (Array.isArray(song)) {
            const mainArtistName = getMainArtistName(song[0].artist);
            const isExpanded = isArtistPresent(mainArtistName, expandedArtists);
            const onClick: ReactEventHandler = (e) => {
              e.stopPropagation();
              if (isExpanded) {
                setExpandedArtists((artists) => artists.filter((artist) => artist !== mainArtistName));
              } else {
                setExpandedArtists((artists) => [...artists, mainArtistName]);
              }
            };

            return (
              <SongItemContainer
                role="button"
                className="bg-opacity-75! active:bg-opacity-100! cursor-pointer bg-black!"
                data-test={`song-group-${mainArtistName}`}
                onClick={onClick}
                {...itemProps}>
                <Language>
                  <ArrowRight
                    className={`text-white transition-transform! duration-200! ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </Language>
                <ArtistTitle>
                  <Title>{mainArtistName}</Title>
                  <Artist data-test={'songs-count'} data-value={`${song.length}`}>
                    {song.length} songs
                  </Artist>
                </ArtistTitle>
                <Action>
                  {isExpanded ? (
                    <ActiveButton data-test="remove-song-button">CLOSE</ActiveButton>
                  ) : (
                    <AddButton data-test="add-song-button">EXPAND</AddButton>
                  )}
                </Action>
              </SongItemContainer>
            );
          }

          const isExpanded = isArtistPresent(getMainArtistName(song.artist), expandedArtists);
          const isOnList = savedSongList.includes(song.id);

          return (
            <SongItemContainer
              className={isOnList ? 'bg-opacity-35! bg-black!' : ''}
              data-test={song.id}
              {...itemProps}>
              {isExpanded && <Language />}
              <Language>
                <Flag language={song.language} />
              </Language>
              <ArtistTitle>
                <Title>{song.title}</Title>
                <Artist>{song.artist}</Artist>
              </ArtistTitle>
              <Action>
                {keyboard?.remote?.includes('select-song') && permissions === 'write' && (
                  <ActiveButton
                    onClick={(e) => {
                      e.stopPropagation();
                      RemoteMicClient.selectSong(song.id);
                    }}
                    data-test="select-song-button">
                    SELECT
                  </ActiveButton>
                )}
                {isOnList ? (
                  <ActiveButton
                    data-test="remove-song-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSong(song.id);
                    }}>
                    -
                  </ActiveButton>
                ) : (
                  <AddButton
                    data-test="add-song-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSong(song.id);
                    }}>
                    +
                  </AddButton>
                )}
              </Action>
            </SongItemContainer>
          );
        }}
      />
    </Container>
  );
}
export default RemoteSongList;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`;

const TopBar = styled.div`
  ${typography};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 12px 10px;
  border-bottom: 1px solid var(--walky-border);
  background: color-mix(in srgb, var(--walky-canvas) 72%, transparent);
`;

const SearchInput = styled(Input)`
  width: 100%;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--walky-surface-raised);
  font-size: 15px;

  input {
    color: var(--walky-ink);
    font-size: 15px;
    font-weight: 800;
  }

  svg {
    color: var(--walky-orange);
    font-size: 20px;
  }
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 58px;
  gap: 6px;
  width: 100%;
`;
const Tab = styled.button`
  border: 1px solid var(--walky-border);
  border-radius: 9px;
  min-width: 0;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  ${typography};
  font-size: 13px;
  font-weight: 900;
  position: relative;
  transition: 160ms ease;

  &[data-active='true'] {
    color: #ffffff;
    background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
    border-color: rgba(244, 90, 10, 0.48);
    box-shadow: 0 8px 18px rgba(244, 90, 10, 0.18);
  }

  &[data-active='false'] {
    box-shadow: inset 0 0 0 1px var(--walky-orange);
  }
`;

const SongItemContainer = styled.div`
  background: color-mix(in srgb, var(--walky-surface-raised) 88%, transparent);
  display: flex;
  align-items: center;
  min-height: 72px;
  padding: 10px 12px;
  gap: 10px;
  border-bottom: 1px solid var(--walky-border);
  overflow: hidden;
  position: relative;
  transition: 120ms ease;

  &:active {
    background: var(--walky-orange-soft);
  }
`;

const Language = styled.div`
  min-width: 3rem;
  img,
  svg {
    height: 3rem;
    width: 3rem;
    object-fit: cover;
    border-radius: 3rem;
    aspect-ratio: 1;
  }
`;

const ArtistTitle = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  overflow: hidden;
`;

const Artist = styled.span`
  color: var(--walky-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Title = styled.span`
  color: var(--walky-ink);
  font-size: 14px;
  font-weight: 900;
  line-height: 1.18;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Action = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const AddButton = styled.button`
  min-width: 42px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid var(--walky-border);
  border-radius: 999px;
  background: var(--walky-canvas);
  color: var(--walky-ink);
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
  transition: 160ms ease;

  &:active {
    color: #ffffff;
    background: var(--walky-orange);
    border-color: var(--walky-orange);
  }
`;
const ActiveButton = styled(AddButton)`
  color: #ffffff;
  background: var(--walky-orange);
  border-color: rgba(244, 90, 10, 0.55);
`;
